import type { LastBrowsedLocation } from './types'
import type { CitiesSlugs } from '@/city/types'
import { setRequestHeaders } from '@/tools/fetcher/client'
import { setTogglesAttributes } from '@/tools/toggles/client/service'

export const updateLastBrowsedLocation = (
  previousLocation: LastBrowsedLocation,
  newLocation: Partial<LastBrowsedLocation>
) => {
  const result: LastBrowsedLocation = {
    cityCode: previousLocation.cityCode,
    countryCode: previousLocation.countryCode,
    locationCoordinates: previousLocation.locationCoordinates,
  }

  if ('countryCode' in newLocation) {
    result.countryCode = newLocation.countryCode || null
  }

  if ('cityCode' in newLocation) {
    result.cityCode = newLocation.cityCode || null
  }

  if ('locationCoordinates' in newLocation) {
    result.locationCoordinates = newLocation.locationCoordinates || null
  } else {
    const shouldResetCoordinates =
      previousLocation.countryCode !== result.countryCode ||
      previousLocation.cityCode !== result.cityCode
    if (shouldResetCoordinates) {
      result.locationCoordinates = null
    }
  }

  return result
}

export const propagateLastBrowsedLocation = (options: Partial<LastBrowsedLocation>) => {
  if ('countryCode' in options) {
    const countryCode = options.countryCode || null
    setRequestHeaders('Glovo-Location-Country-Code', countryCode || '')
  }

  if ('cityCode' in options) {
    const cityCode = options.cityCode || null
    setRequestHeaders('Glovo-Location-City-Code', cityCode || '')
  }

  setTogglesAttributes({
    countryCode: options?.countryCode || '',
    cityCode: options?.cityCode || '',
  })

  if ('locationCoordinates' in options) {
    const locationCoordinates = options.locationCoordinates || null
    const longitude = locationCoordinates ? `${locationCoordinates?.longitude}` : ''
    const latitude = locationCoordinates ? `${locationCoordinates?.latitude}` : ''
    const currentTime = `${new Date().getTime()}`
    setRequestHeaders('Glovo-Delivery-Location-Longitude', longitude)
    setRequestHeaders('Glovo-Delivery-Location-Latitude', latitude)
    setRequestHeaders('Glovo-Delivery-Location-Timestamp', currentTime)
    setRequestHeaders('Glovo-Delivery-Location-Accuracy', '0')
  }
}

export const getLocationFromUrl = (
  url: string,
  citiesSlugs: CitiesSlugs
): LastBrowsedLocation | null => {
  const urlSegments = url?.split('/') || []
  const countryCodeSegment = urlSegments[2]

  if (!countryCodeSegment) {
    return { countryCode: null, cityCode: null, locationCoordinates: null }
  }

  const cityNameSegment = urlSegments[3]

  const isSupportedCountry = Object.keys(citiesSlugs).includes(countryCodeSegment.toUpperCase())
  if (!isSupportedCountry) {
    return null
  }

  let cityCode: string | null = null
  const countryCode: string = countryCodeSegment.toUpperCase()
  if (cityNameSegment) {
    cityCode = citiesSlugs[countryCode]?.[cityNameSegment.toLowerCase()] || null
  }

  return { countryCode, cityCode, locationCoordinates: null }
}
